import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type { CatalogOtherCostVariableGroupUpdateInput, ProjectMethodologyEnum } from 'gql/graphql';
import { useDebounce } from 'uikit';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { isUniqueVariableGroupGql, updateCatalogOtherCostVariableGroupGql } from '../CatalogVariableGroupQueries';

export const useUpdateVariableGroup = ({
    groupName,
    removeInputHandler,
    updateGroupList,
    catalogUniqueId,
    isGlobal = false,
    methodology,
}: {
    groupName: string | null;
    removeInputHandler: () => void;
    updateGroupList: () => void;
    catalogUniqueId: string | undefined;
    isGlobal?: boolean;
    methodology: ProjectMethodologyEnum;
}) => {
    const dispatch = useDispatch();

    const memoizedRequestIds = useMemo(() => ['isUniqueVariableGroup', 'updateCatalogOtherCostVariableGroupGql'], []);

    const [resetUpdateData] = useResetMultipleStoreData({ requestIds: memoizedRequestIds });

    useEffect(
        () => () => {
            resetUpdateData();
        },
        [resetUpdateData],
    );

    const [isUniqueVariableGroupData, isUniqueVariableGroupStatus, , isUniqueVariableGroupRequest] = useRequest({
        requestId: 'isUniqueVariableGroup',
        query: isUniqueVariableGroupGql,
        withEffect: false,
    });

    const debounceRequest = useDebounce(() => {
        if (groupName && catalogUniqueId) {
            dispatch(
                isUniqueVariableGroupRequest({
                    groupName: groupName.trim(),
                    catalogUniqueId,
                    isGlobal,
                    methodology,
                }),
            );
        }
    }, 300);

    useEffect(() => {
        if (groupName?.length) {
            debounceRequest();
        }
    }, [debounceRequest, groupName?.length]);

    const tempSuccessHandler = useCallback(() => {
        removeInputHandler();
        updateGroupList();
        resetUpdateData();
    }, [removeInputHandler, resetUpdateData, updateGroupList]);

    const [, , , updateOtherVariableGroupRequest] = useRequest({
        requestId: 'updateCatalogOtherCostVariableGroup',
        query: updateCatalogOtherCostVariableGroupGql,
        withEffect: false,
        dependenciesUpdater: tempSuccessHandler,
    });

    const updateOtherVariableGroupHandler = useCallback(
        (input: CatalogOtherCostVariableGroupUpdateInput) => {
            dispatch(updateOtherVariableGroupRequest({ input }));
        },
        [updateOtherVariableGroupRequest, dispatch],
    );

    return useMemo(
        () => ({
            isUniqueVariableGroupData: isUniqueVariableGroupData?.isUniqueVariableGroup.result,
            isUniqueVariableGroupStatus,
            updateOtherVariableGroupHandler,
        }),
        [
            updateOtherVariableGroupHandler,
            isUniqueVariableGroupData?.isUniqueVariableGroup.result,
            isUniqueVariableGroupStatus,
        ],
    );
};
